import { useEffect, useState } from "react";
import { api, API_BASE, formatMoney, formatDate, openDownload } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, User, Truck, FileText, Download, TrendingUp, TrendingDown } from "lucide-react";
import ImportButton from "@/components/ImportButton";

export default function CariAccounts() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [stmtOpen, setStmtOpen] = useState(false);
  const [stmtData, setStmtData] = useState(null);
  const [form, setForm] = useState({ name: "", type: "customer", tax_number: "", email: "", phone: "", address: "", currency: "TRY", balance: "" });

  const load = async () => setItems((await api.get("/cari")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/cari", { ...form, balance: Number(form.balance || 0) });
      toast.success("Cari eklendi");
      setOpen(false); setForm({ name: "", type: "customer", tax_number: "", email: "", phone: "", address: "", currency: "TRY", balance: "" });
      load();
    } catch { toast.error("Ekleme başarısız"); }
  };
  const del = async (id) => { if (!window.confirm("Silinsin mi?")) return; await api.delete(`/cari/${id}`); toast.success("Silindi"); load(); };

  const openStatement = async (c) => {
    try {
      const { data } = await api.get(`/cari/${c.id}/statement`);
      setStmtData(data);
      setStmtOpen(true);
    } catch { toast.error("Ekstre yüklenemedi"); }
  };

  const downloadStatementPdf = () => {
    const token = localStorage.getItem("madymark_token");
    openDownload(`/api/cari/${stmtData.cari.id}/statement.pdf`);
  };

  const filtered = filter === "all" ? items : items.filter(i => i.type === filter);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Müşteri & Tedarikçi</div>
          <h1 className="font-display text-4xl font-extrabold text-slate-900 mt-1">Cari Hesaplar</h1>
        </div>
        <div className="flex gap-2">
          <ImportButton kind="cari" label="Excel İçe Aktar" onDone={load} />
          <button data-testid="add-cari" onClick={() => setOpen(true)} className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[#1E2A9C] text-white font-bold hover:bg-[#121A68] shadow-lg shadow-[#1E2A9C]/20"><Plus className="w-4 h-4" /> Yeni Cari</button>
        </div>
      </div>

      <div className="flex gap-2">
        {[{k:"all",l:"Tümü"},{k:"customer",l:"Müşteriler"},{k:"supplier",l:"Tedarikçiler"}].map(t => (
          <button key={t.k} data-testid={`cari-filter-${t.k}`} onClick={() => setFilter(t.k)} className={`px-4 py-2 rounded-full text-sm font-bold transition ${filter === t.k ? "bg-[#1E2A9C] text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{t.l}</button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest">
            <tr>
              <th className="text-left px-6 py-3 font-bold">Tip</th>
              <th className="text-left px-6 py-3 font-bold">Ünvan</th>
              <th className="text-left px-6 py-3 font-bold">VKN</th>
              <th className="text-left px-6 py-3 font-bold">İletişim</th>
              <th className="text-right px-6 py-3 font-bold">Bakiye</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={6} className="text-center py-16 text-slate-400">Cari hesap yok</td></tr>}
            {filtered.map(c => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold ${c.type === "customer" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>
                    {c.type === "customer" ? <User className="w-3 h-3" /> : <Truck className="w-3 h-3" />}
                    {c.type === "customer" ? "Müşteri" : "Tedarikçi"}
                  </span>
                </td>
                <td className="px-6 py-4 font-semibold text-slate-800">{c.name}</td>
                <td className="px-6 py-4 font-mono-num text-slate-600">{c.tax_number}</td>
                <td className="px-6 py-4 text-slate-600 text-xs">{c.email}<br/>{c.phone}</td>
                <td className={`px-6 py-4 text-right font-mono-num font-bold ${c.balance >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{formatMoney(c.balance, c.currency)}</td>
                <td className="px-6 py-4 text-right"><div className="flex justify-end gap-2">
                  <button data-testid={`stmt-${c.id}`} onClick={() => openStatement(c)} className="text-[#1E2A9C] hover:text-[#29B7E3]" title="Ekstre Aç"><FileText className="w-4 h-4" /></button>
                  <button data-testid={`del-cari-${c.id}`} onClick={() => del(c.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 grid place-items-center p-4">
          <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-xl p-8 relative">
            <button type="button" onClick={() => setOpen(false)} className="absolute top-4 right-4"><X className="w-5 h-5" /></button>
            <div className="font-display text-2xl font-extrabold mb-6">Yeni Cari</div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Tip</label>
                <select data-testid="cari-type" value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                  <option value="customer">Müşteri</option><option value="supplier">Tedarikçi</option>
                </select></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Para Birimi</label>
                <select data-testid="cari-currency" value={form.currency} onChange={(e) => setForm({...form, currency: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                  {["TRY","USD","EUR"].map(c => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Ünvan</label>
                <input required data-testid="cari-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">VKN</label>
                <input data-testid="cari-tax" value={form.tax_number} onChange={(e) => setForm({...form, tax_number: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg font-mono-num" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Bakiye</label>
                <input type="number" step="0.01" data-testid="cari-balance" value={form.balance} onChange={(e) => setForm({...form, balance: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg font-mono-num" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">E-posta</label>
                <input type="email" data-testid="cari-email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Telefon</label>
                <input data-testid="cari-phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
              <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Adres</label>
                <textarea data-testid="cari-address" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} rows="2" className="w-full mt-1 px-3 py-2 border rounded-lg"></textarea></div>
            </div>
            <button data-testid="cari-submit" className="w-full mt-6 py-3 bg-[#1E2A9C] text-white rounded-lg font-bold">Kaydet</button>
          </form>
        </div>
      )}
      {stmtOpen && stmtData && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 grid place-items-start overflow-y-auto p-4">
          <div className="bg-white rounded-2xl w-full max-w-5xl my-8 shadow-2xl">
            <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white z-10 rounded-t-2xl">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Cari Ekstresi</div>
                <div className="font-display font-extrabold text-2xl text-slate-900">{stmtData.cari.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {stmtData.cari.type === "customer" ? "Müşteri" : "Tedarikçi"}
                  {stmtData.cari.tax_number && ` · VKN ${stmtData.cari.tax_number}`}
                  {stmtData.cari.email && ` · ${stmtData.cari.email}`}
                </div>
              </div>
              <div className="flex gap-2">
                <button data-testid="stmt-pdf" onClick={downloadStatementPdf} className="px-4 py-2 bg-[#1E2A9C] text-white rounded-lg font-bold text-sm flex items-center gap-1"><Download className="w-4 h-4" /> PDF İndir</button>
                <button onClick={() => setStmtOpen(false)} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
            </div>

            <div className="p-8 space-y-6">
              <div className="grid grid-cols-4 gap-4">
                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Gelir</div>
                  <div className="font-mono-num text-xl font-bold text-emerald-700 mt-2">{formatMoney(stmtData.totals.income, stmtData.cari.currency)}</div>
                </div>
                <div className="p-4 bg-rose-50 rounded-xl border border-rose-100">
                  <div className="text-[10px] uppercase tracking-widest text-rose-600 font-bold flex items-center gap-1"><TrendingDown className="w-3 h-3" /> Gider</div>
                  <div className="font-mono-num text-xl font-bold text-rose-700 mt-2">{formatMoney(stmtData.totals.expense, stmtData.cari.currency)}</div>
                </div>
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="text-[10px] uppercase tracking-widest text-blue-600 font-bold">Bakiye</div>
                  <div className={`font-mono-num text-xl font-bold mt-2 ${stmtData.cari.balance >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{formatMoney(stmtData.cari.balance, stmtData.cari.currency)}</div>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Proforma</div>
                  <div className="font-mono-num text-xl font-bold text-slate-900 mt-2">{stmtData.totals.proforma_count} adet</div>
                  <div className="text-xs text-slate-500">{formatMoney(stmtData.totals.proforma_total, stmtData.cari.currency)}</div>
                </div>
              </div>

              <div>
                <div className="font-display font-bold text-lg mb-3">İşlemler ({stmtData.transactions.length})</div>
                <div className="border rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest">
                      <tr><th className="text-left px-4 py-2 font-bold">Tarih</th><th className="text-left px-4 py-2 font-bold">Tip</th><th className="text-left px-4 py-2 font-bold">Kategori</th><th className="text-left px-4 py-2 font-bold">Açıklama</th><th className="text-right px-4 py-2 font-bold">Tutar</th></tr>
                    </thead>
                    <tbody>
                      {stmtData.transactions.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-400 text-sm">Kayıt yok</td></tr>}
                      {stmtData.transactions.slice(0, 20).map(t => (
                        <tr key={t.id} className="border-t border-slate-100">
                          <td className="px-4 py-2 font-mono-num text-slate-700">{formatDate(t.date)}</td>
                          <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded text-xs font-bold ${t.type === "income" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{t.type === "income" ? "Gelir" : "Gider"}</span></td>
                          <td className="px-4 py-2 text-slate-600">{t.category}</td>
                          <td className="px-4 py-2 text-slate-600">{t.description}</td>
                          <td className={`px-4 py-2 text-right font-mono-num font-bold ${t.type === "income" ? "text-emerald-600" : "text-rose-600"}`}>{t.type === "income" ? "+" : "−"}{formatMoney(t.amount, t.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <div className="font-display font-bold text-lg mb-3">Proformalar ({stmtData.proformas.length})</div>
                <div className="border rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest">
                      <tr><th className="text-left px-4 py-2 font-bold">No</th><th className="text-left px-4 py-2 font-bold">Tarih</th><th className="text-left px-4 py-2 font-bold">Kalem</th><th className="text-right px-4 py-2 font-bold">Tutar</th></tr>
                    </thead>
                    <tbody>
                      {stmtData.proformas.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-slate-400 text-sm">Proforma yok</td></tr>}
                      {stmtData.proformas.map(p => (
                        <tr key={p.id} className="border-t border-slate-100">
                          <td className="px-4 py-2 font-mono-num text-[#1E2A9C] font-bold">{p.number}</td>
                          <td className="px-4 py-2 font-mono-num text-slate-700">{formatDate(p.date)}</td>
                          <td className="px-4 py-2 text-slate-600">{p.lines.length} kalem</td>
                          <td className="px-4 py-2 text-right font-mono-num font-bold text-slate-900">{formatMoney(p.total, p.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <div className="font-display font-bold text-lg mb-3">Yaklaşan Ödeme/Tahsilat ({stmtData.upcoming.length})</div>
                <div className="border rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-widest">
                      <tr><th className="text-left px-4 py-2 font-bold">Başlık</th><th className="text-left px-4 py-2 font-bold">Vade</th><th className="text-left px-4 py-2 font-bold">Tip</th><th className="text-left px-4 py-2 font-bold">Durum</th><th className="text-right px-4 py-2 font-bold">Tutar</th></tr>
                    </thead>
                    <tbody>
                      {stmtData.upcoming.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-400 text-sm">Kayıt yok</td></tr>}
                      {stmtData.upcoming.map(u => (
                        <tr key={u.id} className="border-t border-slate-100">
                          <td className="px-4 py-2 font-semibold text-slate-700">{u.title}</td>
                          <td className="px-4 py-2 font-mono-num text-slate-700">{formatDate(u.due_date)}</td>
                          <td className="px-4 py-2"><span className={`px-2 py-0.5 rounded text-xs font-bold ${u.type === "collection" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>{u.type === "collection" ? "Tahsilat" : "Ödeme"}</span></td>
                          <td className="px-4 py-2 text-slate-600">{u.status}</td>
                          <td className={`px-4 py-2 text-right font-mono-num font-bold ${u.type === "collection" ? "text-emerald-600" : "text-rose-600"}`}>{formatMoney(u.amount, u.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
